import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PolicyIcon from '@mui/icons-material/Policy';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import { LoadingButton } from '@mui/lab';
import Alert from '@mui/material/Alert';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TREASURY_ONBOARDING_POLL_MS,
  TREASURY_READINESS_LABELS,
  TREASURY_RESUBMITTABLE_ACCOUNT_STATUSES,
  TREASURY_RETRY_WATCH_MS,
} from '../../../constants/treasury';
import { FakeButtonLink } from '../../../components/FakeLink';
import { CodeBlock, Field, FieldGrid, SectionCard } from '../../../components/Section';
import { usePermissions } from '../../../hooks';
import {
  useFetchTreasuryAlpacaOnboardingQuery,
  useFetchTreasuryKybReadinessQuery,
  useRetryTreasuryAlpacaOnboardingMutation,
  useRetryTreasuryWorldCheckMutation,
} from '../../../redux/features/treasury/treasury.apiSlice';
import { locations } from '../../../routes/locations';
import { TreasuryAlpacaOnboarding, TreasuryKybReadiness } from '../../../types/treasury';
import * as permissions from '../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../utils/treasury';

type TreasuryAlpacaAccountStepProps = {
  companyId: number;
  /** The owner's madkhol-user id — a link to their user page (status, KYC, addresses, activation). */
  ownerUserId?: number | null;
  /** Called once when the account turns ACTIVE while this step is open, so the page moves on without a refresh. */
  onDone?: () => void;
};

const OWNER_WORLD_CHECK_MISSING = 'OWNER_WORLD_CHECK_MISSING';

const statusColor = (status?: string | null): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  if (!status) return 'default';
  if (status === 'ACTIVE' || status === 'APPROVED' || status === 'DONE') return 'success';
  if (status === 'REJECTED' || status === 'SUBMISSION_FAILED' || status === 'FAILED') return 'error';
  if (status === 'ACTION_REQUIRED') return 'warning';
  return 'info';
};

/**
 * Step 8 — the company Alpaca account. Nothing to fill in: the account opens by itself once nothing is missing. This
 * shows what is still missing, what the latest attempt did and what Alpaca last said, and the two retries. The
 * source of truth is madkhol-alpaca, read through thriftplan every time.
 */
export const TreasuryAlpacaAccountStep = ({ companyId, ownerUserId, onDone }: TreasuryAlpacaAccountStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canRetry = usePermissions({ items: [permissions.Treasury.updateKyb] });
  // Re-read every few seconds while an attempt runs or a retry is being followed (set below, once the data says so).
  const [isPolling, setIsPolling] = useState(false);
  const pollingInterval = isPolling ? TREASURY_ONBOARDING_POLL_MS : 0;
  const readinessQuery = useFetchTreasuryKybReadinessQuery(companyId, { pollingInterval });
  // The same entry CompanyTreasury reads — it moves to the operations once this reads ACTIVE.
  const onboardingQuery = useFetchTreasuryAlpacaOnboardingQuery(companyId, { pollingInterval });
  const [retryTreasuryAlpacaOnboarding, { isLoading: isRetrying }] = useRetryTreasuryAlpacaOnboardingMutation();
  const [retryTreasuryWorldCheck, { isLoading: isRetryingWorldCheck }] = useRetryTreasuryWorldCheckMutation();
  const readiness: TreasuryKybReadiness | null = readinessQuery.data ?? null;
  const onboarding: TreasuryAlpacaOnboarding | null = onboardingQuery.data ?? null;
  const isLoading = readinessQuery.isLoading || onboardingQuery.isLoading;
  // e.g. madkhol-alpaca down (503): show that, never "not opened yet" from a failed read.
  const loadError =
    readinessQuery.isError || onboardingQuery.isError
      ? getTreasuryErrorMessage(readinessQuery.error ?? onboardingQuery.error, 'Failed to load the Alpaca account')
      : null;
  const { refetch: refetchReadiness } = readinessQuery;
  const { refetch: refetchOnboarding } = onboardingQuery;

  const load = useCallback(() => {
    refetchReadiness();
    refetchOnboarding();
  }, [refetchReadiness, refetchOnboarding]);

  const account = onboarding?.account ?? null;
  const attempt = onboarding?.latestAttempt ?? null;

  // Move the page on when the account BECOMES active here — not when the step opens on one that already is (the
  // onboarding view shows this step for an active account too, and calling onDone there would reload in a loop).
  const previousStatus = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const status = account?.status ?? null;
    const wasLoaded = previousStatus.current !== undefined;

    if (wasLoaded && previousStatus.current !== 'ACTIVE' && status === 'ACTIVE') {
      onDone?.();
    }

    if (!isLoading) {
      previousStatus.current = status;
    }
  }, [account?.status, isLoading, onDone]);
  const isAttemptRunning = attempt?.status === 'IN_PROGRESS';
  // After a retry: the run is async (a queue message to madkhol-alpaca), so the attempt changes only a few seconds
  // later. Until it does — or the watch times out — keep re-reading, so the new result shows without a Refresh.
  const [watchFrom, setWatchFrom] = useState<{ updatedAt: string | null; until: number } | null>(null);
  const hasAttemptChanged = watchFrom !== null && (attempt?.updatedAt ?? null) !== watchFrom.updatedAt;
  const isWatchingRetry = watchFrom !== null && !hasAttemptChanged;

  useEffect(() => {
    if (hasAttemptChanged) {
      setWatchFrom(null);
    }
  }, [hasAttemptChanged]);

  // Re-read while an attempt is running or a retry is being followed, so the page follows it to DONE / FAILED.
  useEffect(() => {
    setIsPolling(isAttemptRunning || isWatchingRetry);
  }, [isAttemptRunning, isWatchingRetry]);

  // A followed retry that changes nothing gives up after the watch window.
  useEffect(() => {
    if (!isWatchingRetry || !watchFrom) {
      return undefined;
    }

    const timer = setTimeout(
      () => {
        setWatchFrom(null);
        enqueueSnackbar('Nothing changed yet — madkhol-alpaca may not be running. Press Refresh later.', {
          variant: 'warning',
        });
      },
      Math.max(0, watchFrom.until - Date.now()),
    );

    return () => clearTimeout(timer);
  }, [isWatchingRetry, watchFrom, enqueueSnackbar]);

  const retry = async () => {
    try {
      const response = await retryTreasuryAlpacaOnboarding(companyId).unwrap();
      enqueueSnackbar(
        response?.resubmit ? 'A new application was sent to Alpaca.' : 'Sent — the account is being opened.',
        { variant: 'success' },
      );
      setWatchFrom({ updatedAt: attempt?.updatedAt ?? null, until: Date.now() + TREASURY_RETRY_WATCH_MS });
    } catch (error) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Retry error!'), { variant: 'error' });
    }
  };

  // The mutation refetches the readiness and the account.
  const retryWorldCheck = async () => {
    try {
      await retryTreasuryWorldCheck(companyId).unwrap();
      enqueueSnackbar('World-Check done.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'World-Check retry error!'), { variant: 'error' });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
        }
      >
        {loadError}
      </Alert>
    );
  }

  const missing = readiness?.missing ?? [];
  const isReady = Boolean(readiness?.ready);
  const isResubmittable = Boolean(account && TREASURY_RESUBMITTABLE_ACCOUNT_STATUSES.includes(account.status));
  const isActionRequired = account?.status === 'ACTION_REQUIRED';
  const canPressRetry = canRetry && isReady && !isAttemptRunning && !isWatchingRetry && (!account || isResubmittable);

  return (
    <Stack spacing={2}>
      {/* The account — what Alpaca says now. */}
      <SectionCard
        title="Company Account"
        description="Read from madkhol-alpaca every time; opens by itself once nothing is missing."
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={account?.status ?? 'Not opened yet'}
              color={statusColor(account?.status)}
              variant={account ? 'filled' : 'outlined'}
            />
            {ownerUserId && (
              <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.user(ownerUserId)}>
                Owner #{ownerUserId}
              </FakeButtonLink>
            )}
            <Button size="small" startIcon={<RefreshIcon />} onClick={load}>
              Refresh
            </Button>
          </Stack>
        }
      >
        <Stack spacing={2}>
          {isActionRequired && (
            <Alert severity="warning">
              Alpaca needs more information. This is handled by hand with Alpaca for now — see Alpaca&apos;s reason and
              email.
            </Alert>
          )}
          {isResubmittable && (
            <Alert severity="error">
              The account was {account?.status}. Fix the KYB or documents, verify the company again, type two new party
              emails, then press Retry Company Account — it sends a new application.
            </Alert>
          )}
          <FieldGrid columns={3}>
            <Field label="Alpaca Account ID" value={account?.accountId} />
            <Field label="Alpaca's Reason" value={attempt?.accountStatusReason} />
            <Field
              label="Status Updated At"
              value={attempt?.accountStatusAt ? new Date(attempt.accountStatusAt).toLocaleString() : null}
            />
          </FieldGrid>
          {attempt?.kycResults && (
            <FieldGrid columns={1}>
              <Field label="Alpaca KYC Results" fullWidth>
                <CodeBlock value={JSON.stringify(attempt.kycResults, null, 2)} />
              </Field>
            </FieldGrid>
          )}
        </Stack>
      </SectionCard>

      <Grid container spacing={2} alignItems="stretch">
        {/* The latest attempt — what our run did. */}
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Latest Attempt"
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                {(isAttemptRunning || isWatchingRetry) && <CircularProgress size={16} />}
                {attempt && <Chip size="small" label={attempt.status} color={statusColor(attempt.status)} />}
              </Stack>
            }
            sx={{ height: '100%' }}
          >
            {attempt ? (
              <Stack spacing={2}>
                <FieldGrid>
                  <Field label="Attempt" value={`#${attempt.attempt}`} />
                  <Field label="Updated At" value={new Date(attempt.updatedAt).toLocaleString()} />
                </FieldGrid>
                {attempt.lastError && (
                  <Alert severity={attempt.status === 'FAILED' ? 'error' : 'warning'}>{attempt.lastError}</Alert>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No attempt yet — one starts by itself as soon as nothing is missing.
              </Typography>
            )}
          </SectionCard>
        </Grid>

        {/* What is still missing. */}
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Readiness"
            action={
              <Chip
                size="small"
                color={isReady ? 'success' : 'warning'}
                label={isReady ? 'Ready' : `${missing.length} missing`}
              />
            }
            sx={{ height: '100%' }}
          >
            {isReady ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2">Nothing is missing.</Typography>
              </Stack>
            ) : (
              <List dense disablePadding>
                {missing.map((code) => (
                  <ListItem key={code} disableGutters alignItems="flex-start">
                    <ListItemIcon sx={{ minWidth: 32, mt: '6px' }}>
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={TREASURY_READINESS_LABELS[code] ?? code} secondary={code} />
                  </ListItem>
                ))}
              </List>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {missing.includes(OWNER_WORLD_CHECK_MISSING) && (
          <LoadingButton
            variant="outlined"
            color="primary"
            loading={isRetryingWorldCheck}
            startIcon={<PolicyIcon />}
            onClick={retryWorldCheck}
            disabled={isRetryingWorldCheck || !canRetry}
          >
            Retry World-Check
          </LoadingButton>
        )}
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isRetrying}
          startIcon={<ReplayIcon />}
          onClick={retry}
          disabled={!canPressRetry || isRetrying}
        >
          Retry Company Account
        </LoadingButton>
      </Box>
    </Stack>
  );
};
